import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { api } from '../api/client';
import { translate, useT } from '../i18n/i18n';

export type TwoFactorStatus = { available: boolean; enabled: boolean; backupCodesLeft: number; prompt: boolean; required: boolean };

export function fetchTwoFactor() {
  return api<TwoFactorStatus>('/me/two-factor', { token: true });
}

export function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const t = useT();
  const text = codes.join('\n');
  function save() {
    const url = URL.createObjectURL(new Blob([`EasyLearning backup codes\n\n${text}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'easylearning-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="spaced">
      <p>{t('Keep these backup codes somewhere safe. Each one works once, if you cannot use your phone. They are shown only now.')}</p>
      <ul className="backup-codes" aria-label={t('Backup codes')}>
        {codes.map((c) => <li key={c}><code>{c}</code></li>)}
      </ul>
      <div className="toolbar">
        <button type="button" className="btn secondary" onClick={save}>{t('Save as a file')}</button>
        <button type="button" className="btn" onClick={onDone}>{t('I have saved them')}</button>
      </div>
    </div>
  );
}

export function TwoFactorSetup({ onEnabled, onCancel }: { onEnabled: () => void; onCancel?: () => void }) {
  const t = useT();
  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ uri: string; secret: string }>('/me/two-factor/setup', { method: 'POST', token: true })
      .then(async (s) => {
        const img = await QRCode.toDataURL(s.uri, { margin: 1, width: 220 });
        if (!cancelled) { setSecret(s.secret); setUri(s.uri); setQr(img); }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : translate('That could not be loaded just now.')); });
    return () => { cancelled = true; };
  }, []);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ backupCodes: string[] }>('/me/two-factor/confirm', { method: 'POST', token: true, body: { code: code.trim() } });
      setCodes(r.backupCodes);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  if (codes) return <BackupCodes codes={codes} onDone={onEnabled} />;

  return (
    <form onSubmit={confirm} className="spaced" noValidate>
      {error && <div className="error" role="alert">{error}</div>}
      <ol>
        <li>{t('Install an authenticator app on your phone, such as Google Authenticator or Microsoft Authenticator.')}</li>
        <li>{t('In the app, add an account and scan this code.')}</li>
      </ol>
      <AppGuide />
      {qr ? <img src={qr} width={220} height={220} alt={t('QR code to scan with your authenticator app')} /> : !error && <p className="muted">{t('Loading…')}</p>}
      {uri && (
        <p className="field-hint">
          {t('Using EasyLearning on the same phone as the app?')}{' '}
          <a href={uri}>{t('Open in my authenticator app')}</a>
        </p>
      )}
      {secret && (
        <p className="field-hint">
          {t('Cannot scan it? Type this key into the app instead:')} <code className="two-factor-key">{secret.replace(/(.{4})/g, '$1 ').trim()}</code>
        </p>
      )}
      <div className="field">
        <label htmlFor="two-factor-code">{t('Type the 6-digit code the app shows')}</label>
        <span className="field-hint">{t('The number changes every 30 seconds. Type the one showing now.')}</span>
        <input
          id="two-factor-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      <div className="toolbar">
        <button type="submit" className="btn" disabled={busy || code.length !== 6 || !secret}>
          {busy ? t('Working…') : t('Turn on two-step sign-in')}
        </button>
        {onCancel && <button type="button" className="btn ghost" onClick={onCancel}>{t('Cancel')}</button>}
      </div>
    </form>
  );
}

type GuideApp = 'microsoft' | 'google' | 'iphone';

function AppGuide() {
  const t = useT();
  const [app, setApp] = useState<GuideApp>('microsoft');
  const guides: Record<GuideApp, { name: string; steps: string[] }> = {
    microsoft: {
      name: 'Microsoft Authenticator',
      steps: [
        t('Install Microsoft Authenticator from the App Store (iPhone) or Google Play (Android). It is free.'),
        t('Open it and tap the + button at the top.'),
        t('Choose "Other account (Google, Facebook, etc.)".'),
        t("Point your phone's camera at the square code on this screen."),
        t('EasyLearning appears in the app with a 6-digit number. Type that number below.'),
      ],
    },
    google: {
      name: 'Google Authenticator',
      steps: [
        t('Install Google Authenticator from the App Store (iPhone) or Google Play (Android). It is free.'),
        t('Open it and tap the + button.'),
        t('Choose "Scan a QR code" and point the camera at the square code on this screen.'),
        t('EasyLearning appears in the app with a 6-digit number. Type that number below.'),
      ],
    },
    iphone: {
      name: t('iPhone Passwords app'),
      steps: [
        t('On an iPhone with iOS 18 or later, open the Passwords app. It is already on your iPhone.'),
        t('Tap Codes, then the + button, and scan the square code on this screen.'),
        t('EasyLearning appears with a 6-digit number. Type that number below.'),
      ],
    },
  };
  return (
    <details className="app-guide">
      <summary>{t('Show me how, step by step')}</summary>
      <div className="tabs" role="tablist" aria-label={t('Which app')}>
        {(Object.keys(guides) as GuideApp[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={app === k} className={`tab${app === k ? ' is-active' : ''}`} onClick={() => setApp(k)}>
            {guides[k].name}
          </button>
        ))}
      </div>
      <ol>
        {guides[app].steps.map((step) => <li key={step}>{step}</li>)}
      </ol>
    </details>
  );
}
